import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { WaitlistCard } from "@/components/WaitlistCard";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  Sparkles,
  MessageSquare,
  ArrowRight,
  Check,
  GraduationCap,
  ShieldCheck,
  Zap,
  HelpCircle,
  FlaskConical,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1784065647713-d50e315596d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxzdHVkZW50JTIwc3R1ZHlpbmclMjBtb2Rlcm4lMjBtaW5pbWFsfGVufDB8fHx8MTc4NTc2Njc2OHww&ixlib=rb-4.1.0&q=85";

const HOW_STEPS = [
  {
    icon: MessageSquare,
    title: "Sorunu sor",
    body: "Soruyu kendi cümlelerinle yaz.",
  },
  {
    icon: BookOpen,
    title: "Konuyu birlikte açalım",
    body: "Çözümü ve temel fikri adım adım gör.",
  },
  {
    icon: HelpCircle,
    title: "Takıldığın noktayı bul",
    body: "Eksik ön koşulu ve neden zorlandığını netleştir.",
  },
  {
    icon: ArrowRight,
    title: "Sıradaki adımı gör",
    body: "Ne çalışacağını gör ve kısa bir soruyla anlayıp anlamadığını kontrol et.",
  },
];

const BENEFITS = [
  { title: "Seviyene uygun anlatım", body: "Konu, sınıfına ve sınavına uygun bir dille açıklanır." },
  { title: "Örneklerle pekiştirme", body: "Her açıklama, konuyu somutlaştıran kısa bir örnekle biter." },
  { title: "Anladığını kontrol et", body: "Yanıtın sonunda küçük bir kontrol sorusu ile kavradığını görürsün." },
  { title: "Sohbet geçmişin yanında", body: "Sorduğun sorular ve çözümler tek tıkla panelinde." },
];

// Three-band contest wedge demo — used inside the SuperZeka Intelligence Demo
// section AND in the smaller LGS/TYT/AYT case cards. All content is a
// synthetic authored example — labelled "Sentetik örnek" wherever it appears.
const INTELLIGENCE_DEMO = {
  grade: "9. Sınıf",
  course: "Matematik",
  question:
    "ax² + bx + c = 0 denkleminde diskriminant negatif olunca köklerin gerçek olmamasının sebebi nedir? Bunu bir türlü ezberleyemiyorum.",
  answer:
    "Diskriminant Δ = b² − 4ac köklerin nasıl bulunacağını gösterir. Karekök içindeki sayı negatif olursa gerçek sayılarda sonuç alamazsın; bu yüzden Δ < 0 olduğunda \"gerçek kök yok\" deriz. Ezberlemek yerine formülü çözerek görmek daha kalıcı olur.",
  insights: {
    why: "Ezberliyorsun ama diskriminantın karekök içine girdiğini görmeden anlaman zor. Negatif sayının karekökü gerçek sayılar arasında yok — sıkıntı burada.",
    prereq: "Negatif bir sayının karekökünün gerçek sayılarda tanımsız olduğunu net bilmek.",
    next: "Δ değeri farklı çıkan 3 denklem seç (Δ>0, Δ=0, Δ<0). Her biri için karekök adımını yazarak sonucu kendi elinle üret.",
  },
};

const CASE_CARDS = [
  {
    tag: "LGS · Matematik",
    tone: "border-sky-200 bg-sky-50/40",
    problem:
      "İki basamaklı bir sayının rakamları toplamı 9. Rakamları yer değiştirdiğinde sayı 27 artıyor. Sayı kaçtır?",
    why: "Öğrenci sayıyı 10a+b olarak yazmayı denemeden aritmetik tahmine kaçıyor.",
    prereq: "İki basamaklı bir sayının yer değeri gösterimi: sayı = 10·onlar + birler.",
    next: "Sayı = 10a+b, ters çevrilmişi 10b+a. İki denklem yaz, çöz.",
  },
  {
    tag: "TYT · Fizik",
    tone: "border-amber-200 bg-amber-50/40",
    problem:
      "Sürtünmesiz eğik düzlemde 30° açıda serbest bırakılan cismin ivmesini bulun. g=10 m/s².",
    why: "Öğrenci ağırlığı bileşenlere ayırmıyor; F=ma'yı doğrudan mg ile uyguluyor.",
    prereq: "Ağırlığı eğik düzleme paralel/dik bileşenlerine ayırma (mg·sinθ, mg·cosθ).",
    next: "a = g·sin30° adımını yaz, birim ve işaret kontrolüyle sonucu çıkar.",
  },
  {
    tag: "AYT · Matematik",
    tone: "border-emerald-200 bg-emerald-50/40",
    problem:
      "lim (x→0) sin(3x)/x limitini bulun.",
    why: "Öğrenci L'Hospital'e atlıyor; oysa temel limit özdeşliği daha güvenli.",
    prereq: "sin(kx)/x limitinin x→0 için k'ye eşitlendiği temel özdeşlik.",
    next: "sin(3x)/x = 3·sin(3x)/(3x) şeklinde yaz, sonucu 3 olarak oku.",
  },
];

const FAQS = [
  {
    q: "SuperZeka ödevimi benim yerime mi yapar?",
    a: "Hayır. SuperZeka doğrudan çözümü verir, ardından takıldığın noktayı ve öğrenmen gereken adımı gösterir. Amacımız kopya değil, anlamana yardımcı olmak.",
  },
  {
    q: "Hangi dersleri destekliyor?",
    a: "Şu an odağımız sayısal sınavlar: LGS için Matematik ve Fen Bilimleri; TYT ve AYT için Matematik, Fizik, Kimya ve Biyoloji.",
  },
  {
    q: "SuperZeka her seferinde neden takıldığımı kesin olarak bilir mi?",
    a: "Kesin bir teşhis vaat etmiyoruz. Uygun olduğunda muhtemel takılma noktanı, bilmen gereken ön koşulu ve sıradaki adımı özetleriz; bunlar kısa pedagojik gözlemlerdir.",
  },
  {
    q: "Ücretsiz plan gerçekten ücretsiz mi?",
    a: "Evet. Ücretsiz plan ile her gün belirli sayıda soru sorabilirsin. Premium seçenekler hazır olduğunda ayrıca duyurulacak.",
  },
  {
    q: "Verilerim güvende mi?",
    a: "Kişisel verilerin KVKK kapsamında korunur. Ayrıntılar için KVKK sayfamıza bakabilirsin.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-700 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#1E3A8A]" />
              LGS · TYT · AYT için tasarlandı
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[2rem] xl:text-[2.25rem] font-bold text-zinc-900 tracking-tight leading-[1.2]">
              Sadece doğru cevabı vermekle kalmaz.<br className="hidden lg:inline" /> Takıldığında, <span className="text-[#1E3A8A]">neden takıldığını</span> da gösterir.<br className="hidden lg:inline" /> Sonra sıradaki <span className="italic font-medium">doğru adımı.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-xl leading-relaxed">
              SuperZeka, sayısal sorularda yalnızca çözümü göstermekle kalmaz. Takıldığın
              noktayı anlamana, eksik ön koşulu fark etmene ve bir sonraki öğrenme adımını
              belirlemene yardımcı olur.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/demo" data-testid="hero-cta-demo">
                <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-7 text-base">
                  Demoyu Dene
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/kayit" data-testid="hero-cta-primary">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base border-zinc-300">
                  Ücretsiz Kayıt
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> Kredi kartı gerekmez</div>
              <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> Demo için kayıt yok</div>
              <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> LGS · TYT · AYT sayısal</div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#1E3A8A]/10 to-transparent rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-[0_20px_60px_rgba(30,58,138,0.08)]">
                <div className="p-4 border-b border-zinc-100 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                  </div>
                  <div className="ml-auto text-xs text-zinc-500 font-medium">SuperZeka · 9. Sınıf · Matematik</div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="rounded-2xl bg-zinc-50 border border-zinc-100 px-4 py-3 text-sm text-zinc-800">
                    Diskriminant negatif olunca neden gerçek kök yok, bir türlü ezberleyemiyorum.
                  </div>
                  <div className="rounded-2xl bg-white border border-zinc-200 px-4 py-3 text-sm text-zinc-800 leading-relaxed">
                    Δ = b² − 4ac karekök içine giriyor. Karekökün içindeki sayı negatif olursa
                    gerçek sayılarda sonuç yoktur — bu yüzden Δ &lt; 0 durumunda &quot;gerçek kök yok&quot; deriz.
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2">
                      <div className="text-[9px] font-semibold uppercase tracking-widest text-amber-700">Neden takıldın</div>
                      <div className="mt-0.5 text-[11px] text-amber-900 leading-tight">Ezber var, ama karekök adımı görünmüyor.</div>
                    </div>
                    <div className="rounded-xl border border-sky-200 bg-sky-50 px-2.5 py-2">
                      <div className="text-[9px] font-semibold uppercase tracking-widest text-sky-700">Ön koşul</div>
                      <div className="mt-0.5 text-[11px] text-sky-900 leading-tight">Negatif sayının karekökü tanımsız.</div>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-2">
                      <div className="text-[9px] font-semibold uppercase tracking-widest text-emerald-700">Sıradaki adım</div>
                      <div className="mt-0.5 text-[11px] text-emerald-900 leading-tight">3 denklem çöz: Δ&gt;0, Δ=0, Δ&lt;0.</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-400 pt-1 flex items-center gap-1">
                    <FlaskConical className="w-3 h-3" />
                    Sentetik örnek · SuperZeka Intelligence Demo
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaitlistCard />

      {/* Sayısal Scope — 3-card tier section (moved above Intelligence Demo per Track A–J B2=i ordering) */}
      <section id="dersler" className="py-16 lg:py-20 border-b border-zinc-200" data-testid="landing-sayisal-tier-cards">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Kapsam</div>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
            LGS · TYT · AYT sayısal.
          </h2>
          <p className="mt-3 text-zinc-600 max-w-2xl">
            Tek üyelikle üç sınavın sayısal dersleri. Gereksiz ders kalabalığı yok; odağımız
            sınavlarda ihtiyaç duyduğun sayısal alanlar.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-6" data-testid="landing-tier-card-LGS_SAY">
              <div className="text-xs text-sky-700 font-semibold uppercase tracking-wider mb-2">LGS</div>
              <div className="text-lg font-semibold text-zinc-900">Sayısal</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Matematik", "Fen Bilimleri"].map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full bg-white border border-sky-200 text-sm text-sky-900 font-medium">{c}</span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-6" data-testid="landing-tier-card-TYT_SAY">
              <div className="text-xs text-amber-700 font-semibold uppercase tracking-wider mb-2">TYT</div>
              <div className="text-lg font-semibold text-zinc-900">Sayısal</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Matematik", "Fizik", "Kimya", "Biyoloji"].map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full bg-white border border-amber-200 text-sm text-amber-900 font-medium">{c}</span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="landing-tier-card-AYT_SAY">
              <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mb-2">AYT</div>
              <div className="text-lg font-semibold text-zinc-900">Sayısal</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Matematik", "Fizik", "Kimya", "Biyoloji"].map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full bg-white border border-emerald-200 text-sm text-emerald-900 font-medium">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence Demo — the visible wedge */}
      <section
        id="zeka"
        className="py-20 lg:py-24 border-b border-zinc-200 bg-white"
        data-testid="intelligence-demo-section"
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/5 border border-[#1E3A8A]/15 text-xs font-medium text-[#1E3A8A]">
                <Sparkles className="w-3.5 h-3.5" />
                SuperZeka Intelligence Demo
              </div>
              <h2 className="mt-4 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
                Bir soruda neden takıldığını birlikte bulalım.
              </h2>
              <p className="mt-3 text-zinc-600 leading-relaxed">
                Önce soruyu açıklarız. Ardından takıldığın noktayı, bilmen gereken ön koşulu
                ve sıradaki adımı kısa ve anlaşılır biçimde gösteririz. Aşağıdaki örnek gerçek
                bir öğrenciden alınmadı — demo amaçlı yazılmış bir tanıtım.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-xs font-medium text-zinc-500">
              <FlaskConical className="w-3.5 h-3.5" />
              Sentetik örnek
            </div>
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-6 items-start">
            {/* Chat mock */}
            <div className="lg:col-span-7 rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-[0_20px_60px_rgba(30,58,138,0.06)]">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2 text-xs font-medium text-zinc-500">
                <span>{INTELLIGENCE_DEMO.grade}</span>
                <span aria-hidden>·</span>
                <span>{INTELLIGENCE_DEMO.course}</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#1E3A8A] text-white px-4 py-2.5 text-sm leading-relaxed">
                    {INTELLIGENCE_DEMO.question}
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 shrink-0 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#1E3A8A]" />
                  </div>
                  <div className="flex-1 rounded-2xl rounded-tl-md bg-white border border-zinc-200 px-4 py-3 text-sm leading-relaxed text-zinc-800">
                    {INTELLIGENCE_DEMO.answer}
                  </div>
                </div>
              </div>
            </div>

            {/* Three insight bands */}
            <div className="lg:col-span-5 space-y-3" data-testid="intelligence-demo-bands">
              <InsightBand
                icon={HelpCircle}
                label="NEDEN TAKILDIN"
                text={INTELLIGENCE_DEMO.insights.why}
                tone="bg-amber-50 border-amber-200 text-amber-900"
                testid="demo-insight-why"
              />
              <InsightBand
                icon={GraduationCap}
                label="EKSİK ÖN KOŞUL"
                text={INTELLIGENCE_DEMO.insights.prereq}
                tone="bg-sky-50 border-sky-200 text-sky-900"
                testid="demo-insight-prereq"
              />
              <InsightBand
                icon={ArrowRight}
                label="SIRADAKİ ADIM"
                text={INTELLIGENCE_DEMO.insights.next}
                tone="bg-emerald-50 border-emerald-200 text-emerald-900"
                testid="demo-insight-next"
              />
              <Link
                to="/demo"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E3A8A] hover:underline"
                data-testid="intelligence-demo-cta"
              >
                Demoyu kendi sorunla dene
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Numerical exam case cards */}
      <section
        id="ornekler"
        className="py-20 lg:py-24 border-b border-zinc-200"
        data-testid="case-cards-section"
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">
              Örnekler
            </div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
              Sayısal derslerde nasıl yardımcı olur?
            </h2>
            <p className="mt-3 text-zinc-600">
              LGS, TYT ve AYT&apos;den üç kısa örnek. Her biri, çözümün yanında öğrencinin
              nerede zorlanabileceğini ve hangi adıma dönmesinin yararlı olabileceğini gösterir.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {CASE_CARDS.map((c, i) => (
              <div
                key={c.tag}
                data-testid={`case-card-${i + 1}`}
                className={`rounded-2xl border ${c.tone} p-5`}
              >
                <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-700">
                  {c.tag}
                </div>
                <div className="mt-3 text-sm font-medium text-zinc-900 leading-relaxed">
                  {c.problem}
                </div>
                <div className="mt-5 space-y-2">
                  <MicroBand label="Neden takıldın" text={c.why} />
                  <MicroBand label="Ön koşul" text={c.prereq} />
                  <MicroBand label="Sıradaki adım" text={c.next} accent />
                </div>
                <div className="mt-4 text-[10px] text-zinc-500 flex items-center gap-1">
                  <FlaskConical className="w-3 h-3" />
                  Sentetik örnek
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="nasil" className="py-24 lg:py-28 border-b border-zinc-200" data-testid="how-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Nasıl Çalışır?</div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
              Öğrenme akışı nasıl işler?
            </h2>
            <p className="mt-3 text-zinc-600">Sorudan sıradaki adıma dört adımlık bir akış.</p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_STEPS.map((s, i) => (
              <div
                key={s.title}
                className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-zinc-300 transition-colors"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <div className="mt-4 text-xs text-zinc-400 font-mono">0{i + 1}</div>
                <h3 className="mt-1 font-display font-semibold text-lg text-zinc-900">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 lg:py-28 border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Faydalar</div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
              Anlaşılır anlatım. Doğru sırada öğrenme.
            </h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              SuperZeka, sayısal konuları seviyene uygun ve açık bir dille anlatır. Amaç
              yalnızca cevabı göstermek değil; eksik noktayı fark edip öğrenmeye doğru
              yerden devam etmene yardımcı olmaktır.
            </p>
            <Link to="/kayit" className="inline-block mt-6" data-testid="benefits-cta">
              <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                Denemeye Başla
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-2xl border border-zinc-200 p-5">
                <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center mb-3">
                  <Check className="w-4 h-4 text-[#10B981]" />
                </div>
                <h3 className="font-display font-semibold text-zinc-900">{b.title}</h3>
                <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="planlar" className="py-24 lg:py-28 border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Planlar</div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
              Ücretsiz başla. Premium seçenekler yakında.
            </h2>
            <p className="mt-3 text-zinc-600">
              Demoyu deneyebilir veya ücretsiz hesap açabilirsin. Premium özellikler hazır olduğunda ayrıca duyurulacak.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-zinc-200 bg-white p-8" data-testid="plan-free">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-xl">Ücretsiz</h3>
                <span className="text-xs text-zinc-500">Sınırlı</span>
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-display font-bold">0₺</span>
                <span className="text-zinc-500 text-sm">/ay</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Günde 10 AI mesajı</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> LGS · TYT · AYT sayısal dersleri</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Sohbet geçmişi</li>
              </ul>
              <Link to="/kayit" className="block mt-8" data-testid="plan-free-cta">
                <Button variant="outline" className="w-full rounded-full">Ücretsiz Başla</Button>
              </Link>
            </div>

            <div className="rounded-2xl border-2 border-[#1E3A8A] bg-white p-8 relative" data-testid="plan-premium">
              <div className="absolute -top-3 left-8 px-2.5 py-0.5 rounded-full bg-[#1E3A8A] text-white text-xs font-semibold">
                Öneri
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-xl">Premium</h3>
                <Zap className="w-4 h-4 text-[#1E3A8A]" />
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-display font-bold">Yakında</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Yüksek günlük mesaj limiti</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Öncelikli erişim</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Tam sohbet geçmişi</li>
              </ul>
              <Button disabled className="w-full mt-8 rounded-full bg-[#1E3A8A] text-white opacity-70">
                Yakında
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="sss" className="py-24 lg:py-28 border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">SSS</div>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight">
            Sıkça sorulan sorular.
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="border-zinc-200">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left hover:no-underline text-zinc-900 font-medium">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <ShieldCheck className="w-8 h-8 text-[#1E3A8A] mx-auto" />
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight">
            Takıldığın soruyu getir. Birlikte nereden devam edeceğini bulalım.
          </h2>
          <p className="mt-4 text-zinc-600 max-w-xl mx-auto">
            Demoyu hemen deneyebilir veya ücretsiz hesabını oluşturabilirsin.
          </p>
          <Link to="/kayit" className="inline-block mt-8" data-testid="final-cta">
            <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-8 text-base">
              Ücretsiz Başla
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function InsightBand({ icon: Icon, label, text, tone, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex gap-3 rounded-xl border px-4 py-3 ${tone}`}
    >
      <div className="pt-0.5">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80">
          {label}
        </div>
        <div className="mt-0.5 text-sm leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

function MicroBand({ label, text, accent = false }) {
  return (
    <div className="pl-3 border-l-2 border-zinc-300">
      <div
        className={`text-[10px] font-semibold uppercase tracking-widest ${
          accent ? "text-emerald-700" : "text-zinc-500"
        }`}
      >
        {label}
      </div>
      <div className="mt-0.5 text-xs text-zinc-700 leading-relaxed">{text}</div>
    </div>
  );
}
